import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { setAccessToken } from '../services/api';
import { getUserMenuV2 } from '../services/menuService';
import { ApiMenuItem, AuthContextType, User } from '../types';
import { PropertyArea } from '../types/property';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<PropertyArea[]>([]);
  const [loadingPropertyAreas] = useState(false);
  const [userMenu, setUserMenu] = useState<ApiMenuItem[]>([]);

  const fetchUserMenu = useCallback(async () => {
    try {
      const response = await getUserMenuV2();
      setUserMenu(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch user menu:", error);
      setUserMenu([]);
    }
  }, []);

  const processUserData = useCallback((userData: any) => {
    if (!userData) {
      setUser(null);
      setPropertyAreas([]);
      setPermissions([]);
      return;
    }

    setUser(userData);

    const areas = userData.user_property_areas?.map((upa: any) => upa.property_area).filter(Boolean) || [];
    setPropertyAreas(areas);

    const userPermissions = userData.role?.permissions?.map((p: any) => p.name) || [];
    setPermissions(userPermissions);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Set token for API calls
          setAccessToken(token);

          // Immediately process user from localStorage for faster UI response
          processUserData(JSON.parse(storedUser));

          // Then, fetch fresh user data and menu in the background
          const response = await api.get('/profile');
          const freshUser = response.data.data;

          // Update localStorage and state with the latest user data
          localStorage.setItem('user', JSON.stringify(freshUser));
          processUserData(freshUser);

          await fetchUserMenu();
        } catch (error) {
          console.error("Failed to initialize auth:", error);
          logout(); // If token validation fails, log the user out
        } finally {
          setLoading(false); // Stop loading only after all async ops are done
        }
      } else {
        setLoading(false); // Stop loading if there's no token
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('auth/login', { email, password });
      const { token, user } = response.data.data;

      setAccessToken(token);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user)); // Simpan data user

      processUserData(user);
      await fetchUserMenu();

      return { success: true };
    } catch (error: any) {
      console.error("Login failed:", error);
      // Jika tidak ada respons dari server (error jaringan, CORS, dll.)
      if (!error.response) {
        return {
          success: false,
          error: { message: "Tidak dapat terhubung ke server. Silakan periksa koneksi Anda." }
        };
      }
      // Jika ada respons dari server (misal: kredensial salah)
      return {
        success: false,
        error: error.response?.data || { message: 'Terjadi kesalahan saat login.' }
      };
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await api.post('auth/register', userData);
      const { token, user } = response.data.data;

      setAccessToken(token);
      localStorage.setItem('token', token);
      processUserData(user);
      await fetchUserMenu();

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setAccessToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user'); // Menghapus data user dari localStorage
    document.cookie = 'refresh_token=; Max-Age=0; path=/; domain=' + window.location.hostname;

    processUserData(null);
    setUserMenu([]);
  };

  const hasPermission = (permissionName: string) => {
    return permissions.includes(permissionName);
  };

  const value: AuthContextType = {
    user,
    setUser,
    token: null,
    loading,
    permissions,
    hasPermission,
    propertyAreas,
    loadingPropertyAreas,
    userMenu,
    refreshMenu: fetchUserMenu,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

